package com.milta.feature.profile.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.phone.PhoneNumber
import com.milta.feature.auth.data.remote.ApiException
import com.milta.feature.profile.domain.usecase.ConfirmAccountDeletionUseCase
import com.milta.feature.profile.domain.usecase.ConfirmPhoneChangeUseCase
import com.milta.feature.profile.domain.usecase.GetMyDriverLinkCodeUseCase
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.RequestAccountDeletionUseCase
import com.milta.feature.profile.domain.usecase.RequestPhoneChangeUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val getProfile: GetProfileUseCase,
    private val updateLanguage: UpdateLanguageUseCase,
    private val requestPhoneChange: RequestPhoneChangeUseCase,
    private val confirmPhoneChange: ConfirmPhoneChangeUseCase,
    private val requestAccountDeletion: RequestAccountDeletionUseCase,
    private val confirmAccountDeletion: ConfirmAccountDeletionUseCase,
    private val getMyDriverLinkCode: GetMyDriverLinkCodeUseCase,
) : MviViewModel<ProfileState, ProfileIntent>(ProfileState()) {

    private val _navEvent = Channel<ProfileNavEvent>(Channel.BUFFERED)
    val navEvent = _navEvent.receiveAsFlow()

    // Cancelled/restarted on every tap so repeated copies keep resetting the same 2s window
    // instead of stacking multiple pending resets.
    private var codeCopiedResetJob: Job? = null

    init {
        onIntent(ProfileIntent.LoadProfile)
    }

    override fun onIntent(intent: ProfileIntent) {
        when (intent) {
            ProfileIntent.LoadProfile -> loadProfile()

            ProfileIntent.OpenLanguageDialog -> setState {
                copy(showLanguageDialog = true, selectedLanguage = language ?: "en", error = null)
            }
            ProfileIntent.DismissLanguageDialog -> setState {
                copy(showLanguageDialog = false)
            }
            is ProfileIntent.SelectLanguage -> setState {
                copy(selectedLanguage = intent.tag)
            }
            ProfileIntent.ConfirmLanguageChange -> saveLanguage()

            ProfileIntent.OpenPhoneDialog -> setState {
                copy(
                    showPhoneDialog = true,
                    phoneStep = PhoneChangeStep.Number,
                    phoneDialCode = "+32",
                    phoneLocalNumber = "",
                    otpInput = "",
                    error = null,
                )
            }
            ProfileIntent.DismissPhoneDialog -> setState {
                copy(showPhoneDialog = false, phoneStep = PhoneChangeStep.Number, otpInput = "")
            }
            is ProfileIntent.PhoneDialCodeChanged -> setState { copy(phoneDialCode = intent.value) }
            is ProfileIntent.PhoneLocalNumberChanged -> setState { copy(phoneLocalNumber = intent.value, error = null) }
            ProfileIntent.RequestPhoneChange -> requestPhone()
            is ProfileIntent.OtpInputChanged -> setState { copy(otpInput = intent.value, error = null) }
            ProfileIntent.ConfirmPhoneChange -> confirmPhone()

            ProfileIntent.OpenDeleteAccountDialog -> setState {
                copy(showDeleteDialog = true, deleteStep = DeleteAccountStep.Confirm, deleteOtpInput = "", error = null)
            }
            ProfileIntent.DismissDeleteAccountDialog -> setState {
                copy(showDeleteDialog = false, deleteStep = DeleteAccountStep.Confirm, deleteOtpInput = "")
            }
            ProfileIntent.RequestAccountDeletion -> requestDeletion()
            is ProfileIntent.DeleteOtpInputChanged -> setState { copy(deleteOtpInput = intent.value, error = null) }
            ProfileIntent.ConfirmAccountDeletion -> confirmDeletion()

            ProfileIntent.CopyLinkedCode -> {
                setState { copy(codeCopied = true) }
                codeCopiedResetJob?.cancel()
                codeCopiedResetJob = scope.launch {
                    delay(2000)
                    setState { copy(codeCopied = false) }
                }
            }
            ProfileIntent.ClearError -> setState { copy(error = null) }
            ProfileIntent.ClearSuccess -> setState { copy(successMessage = null) }
        }
    }

    private fun loadProfile() {
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            getProfile().fold(
                onSuccess = { profile ->
                    setState {
                        copy(
                            isLoading = false,
                            firstName = profile.firstName,
                            lastName = profile.lastName,
                            phoneNumber = profile.phoneNumber,
                            language = profile.language,
                            roles = profile.roles,
                            selectedLanguage = profile.language ?: "en",
                        )
                    }
                    loadLinkCode()
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        e is ApiException -> "server_error"
                        else -> "offline"
                    }
                    setState { copy(isLoading = false, error = code) }
                },
            )
        }
    }

    // MIL-49 — separate coroutine, not chained into loadProfile()'s fold: the linking code is not
    // critical to display the rest of the profile, so a failure here (e.g. offline) must not block
    // the screen — linkedCode simply stays at its empty default (see ProfileState).
    private fun loadLinkCode() {
        scope.launch {
            getMyDriverLinkCode().onSuccess { code ->
                setState { copy(linkedCode = code) }
            }
        }
    }

    private fun saveLanguage() {
        val tag = currentState.selectedLanguage
        if (tag.isBlank()) {
            setState { copy(error = "language_required") }
            return
        }
        if (tag.length > 10) {
            setState { copy(error = "language_too_long") }
            return
        }
        scope.launch {
            setState { copy(isLanguageSaving = true, error = null) }
            updateLanguage(tag).fold(
                onSuccess = {
                    setState {
                        copy(
                            isLanguageSaving = false,
                            showLanguageDialog = false,
                            language = tag,
                            successMessage = "language_updated",
                        )
                    }
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.statusCode == 400 -> "validation_error"
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        else -> "server_error"
                    }
                    setState { copy(isLanguageSaving = false, error = code) }
                },
            )
        }
    }

    private fun requestPhone() {
        val normalized = PhoneNumber.normalize(currentState.phoneDialCode + currentState.phoneLocalNumber.trim())
        scope.launch {
            setState { copy(isPhoneRequestSaving = true, error = null) }
            requestPhoneChange(normalized).fold(
                onSuccess = {
                    setState {
                        copy(
                            isPhoneRequestSaving = false,
                            phoneStep = PhoneChangeStep.Otp,
                            pendingPhoneNumber = normalized,
                            otpInput = "",
                        )
                    }
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.statusCode == 409 -> "phone_conflict"
                        e is ApiException && e.statusCode == 400 -> "validation_error"
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        else -> "server_error"
                    }
                    setState { copy(isPhoneRequestSaving = false, error = code) }
                },
            )
        }
    }

    private fun confirmPhone() {
        scope.launch {
            setState { copy(isPhoneConfirmSaving = true, error = null) }
            // ConfirmPhoneChangeUseCase validates the OTP length via require() before ever calling
            // the repository, so it can throw synchronously instead of returning Result.failure —
            // runCatching folds that into the same failure path as a real API error.
            runCatching { confirmPhoneChange(currentState.otpInput) }.getOrElse { Result.failure(it) }.fold(
                onSuccess = {
                    setState {
                        copy(
                            isPhoneConfirmSaving = false,
                            showPhoneDialog = false,
                            phoneStep = PhoneChangeStep.Number,
                            phoneNumber = pendingPhoneNumber,
                            otpInput = "",
                            successMessage = "phone_updated",
                        )
                    }
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.message == "error.otp.expired" -> "otp_expired"
                        e is ApiException && e.message == "error.unauthorized" -> "otp_invalid"
                        e is ApiException && e.statusCode == 409 -> "phone_conflict"
                        e is ApiException && e.statusCode == 400 -> "validation_error"
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        else -> "server_error"
                    }
                    setState { copy(isPhoneConfirmSaving = false, error = code) }
                },
            )
        }
    }

    private fun requestDeletion() {
        scope.launch {
            setState { copy(isDeleteRequestSaving = true, error = null) }
            requestAccountDeletion().fold(
                onSuccess = {
                    setState { copy(isDeleteRequestSaving = false, deleteStep = DeleteAccountStep.Otp, deleteOtpInput = "") }
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.message == "error.conflict.last_enterprise_owner" -> "last_enterprise_owner"
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        e is ApiException -> "server_error"
                        else -> "offline"
                    }
                    setState { copy(isDeleteRequestSaving = false, error = code) }
                },
            )
        }
    }

    private fun confirmDeletion() {
        scope.launch {
            setState { copy(isDeleteConfirmSaving = true, error = null) }
            // Same defensive fold as confirmPhone() — ConfirmAccountDeletionUseCase's require() can
            // throw synchronously instead of returning Result.failure.
            runCatching { confirmAccountDeletion(currentState.deleteOtpInput) }.getOrElse { Result.failure(it) }.fold(
                onSuccess = {
                    setState { copy(isDeleteConfirmSaving = false, showDeleteDialog = false) }
                    _navEvent.send(ProfileNavEvent.AccountDeleted)
                },
                onFailure = { e ->
                    val code = when {
                        e is ApiException && e.message == "error.otp.expired" -> "otp_expired"
                        e is ApiException && e.message == "error.unauthorized" -> "otp_invalid"
                        e is ApiException && e.message == "error.conflict.last_enterprise_owner" -> "last_enterprise_owner"
                        e is ApiException && e.statusCode == 400 -> "validation_error"
                        e is ApiException && e.statusCode == 404 -> "profile_not_found"
                        else -> "server_error"
                    }
                    setState { copy(isDeleteConfirmSaving = false, error = code) }
                },
            )
        }
    }
}
